// Builds a single printable "report completo" from /api/report data and opens the print dialog.
const esc = (s) => String(s ?? "").replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]));
const num = (v) => { const n = parseFloat(String(v ?? "").replace(",", ".").replace(/[^0-9.\-]/g, "")); return Number.isFinite(n) ? n : null; };

export const REPORT_SECTIONS = [
  { key: "profile", label: "Profilo e fabbisogni" },
  { key: "insights", label: "Considerazioni stile di vita" },
  { key: "diet", label: "Dieta settimanale" },
  { key: "recipes", label: "Ricette dei pasti" },
  { key: "supplements", label: "Integratori" },
  { key: "blood", label: "Analisi del sangue" },
  { key: "weight", label: "Andamento del peso" },
];

// Minimal inline SVG line chart. points: [{label, value}]
function svgLine(points, stroke = "#2E5BFF") {
  if (!points || points.length < 2) return "";
  const w = 520, h = 150, pad = 34;
  const vals = points.map((p) => p.value);
  const min = Math.min(...vals), max = Math.max(...vals);
  const span = (max - min) || 1;
  const step = (w - 2 * pad) / (points.length - 1);
  const xy = points.map((p, i) => [pad + i * step, h - pad - ((p.value - min) / span) * (h - 2 * pad)]);
  const poly = xy.map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`).join(" ");
  const dots = xy.map(([x, y], i) =>
    `<circle cx="${x.toFixed(1)}" cy="${y.toFixed(1)}" r="3" fill="${stroke}"/>` +
    `<text x="${x.toFixed(1)}" y="${(y - 8).toFixed(1)}" font-size="10" text-anchor="middle" fill="#334">${esc(points[i].value)}</text>`).join("");
  const labels = points.map((p, i) =>
    `<text x="${(pad + i * step).toFixed(1)}" y="${h - 10}" font-size="9" text-anchor="middle" fill="#889">${esc(p.label)}</text>`).join("");
  return `<svg width="${w}" height="${h}" viewBox="0 0 ${w} ${h}"><polyline points="${poly}" fill="none" stroke="${stroke}" stroke-width="2"/>${dots}${labels}</svg>`;
}

export function buildReportHtml(r, sections, opts = {}) {
  const on = (k) => !sections || sections[k];
  const title = opts.title || "Report nutrizionale completo";
  const p = r.profile || {};
  const t = r.targets || {};
  const ins = r.insights || {};
  const supps = (r.supplements && r.supplements.supplements) || [];

  const kv = (label, val) => val != null && val !== "" ? `<div><span class="k">${esc(label)}:</span> ${esc(val)}</div>` : "";
  const list = (arr) => (arr && arr.length) ? `<ul>${arr.map((x) => `<li>${esc(x)}</li>`).join("")}</ul>` : "<p class='muted'>—</p>";

  const profileBlock = `
    <div class="grid2">
      ${kv("Sesso", p.sex)} ${kv("Età", p.age)}
      ${kv("Altezza", p.height_cm ? p.height_cm + " cm" : "")} ${kv("Peso", p.weight_kg ? p.weight_kg + " kg" : "")}
      ${kv("Peso obiettivo", p.target_weight_kg ? p.target_weight_kg + " kg" : "")} ${kv("Obiettivo", t.goal_label)}
      ${kv("Stile alimentare", p.dietary_style)} ${kv("Attività", p.activity_level)}
      ${kv("Patologie", p.pathologies)} ${kv("Farmaci", p.medications)}
      ${kv("Allergie", p.allergies)} ${kv("Intolleranze", p.intolerances)}
    </div>
    <div class="targets">
      <span>Fabbisogno: <b>${esc(t.target_kcal || "—")} kcal</b></span>
      <span>Proteine: <b>${esc(t.protein_g || "—")}g</b></span>
      <span>Carboidrati: <b>${esc(t.carbs_g || "—")}g</b></span>
      <span>Grassi: <b>${esc(t.fat_g || "—")}g</b></span>
    </div>`;

  const insightsBlock = (ins.summary || (ins.strengths || []).length) ? `
    ${ins.summary ? `<p>${esc(ins.summary)}</p>` : ""}
    <div class="grid2">
      <div><h4>Punti di forza</h4>${list(ins.strengths)}</div>
      <div><h4>Aree da migliorare</h4>${list(ins.improvements)}</div>
      <div><h4>Abitudini consigliate</h4>${list(ins.habits)}</div>
      <div><h4>Da monitorare</h4>${list(ins.focus_markers)}</div>
    </div>` : "<p class='muted'>Non ancora generate.</p>";

  const suppByMeal = (meal) => supps.filter((s) => s.meal === meal)
    .map((s) => `<div class="supp">💊 ${esc(s.name)} · ${esc(s.dosage || "")}</div>`).join("");

  const recipes = r.recipes || {};
  const recipeFor = (mealName) => {
    if (!on("recipes")) return "";
    const rec = recipes[String(mealName || "").toLowerCase()];
    if (!rec) return "";
    const ing = (rec.ingredients || []).map((x) => `<li>${esc(x)}</li>`).join("");
    const steps = (rec.steps || []).map((x) => `<li>${esc(x)}</li>`).join("");
    const n = rec.nutrition || {};
    const nut = (n.kcal != null) ? `<div class="recnut">Per porzione: ${esc(n.kcal)} kcal · P ${esc(n.protein_g ?? "—")}g · C ${esc(n.carbs_g ?? "—")}g · G ${esc(n.fat_g ?? "—")}g</div>` : "";
    return `<div class="recipe"><div class="rectitle">👨‍🍳 Ricetta${rec.prep_min != null ? ` · prep ${esc(rec.prep_min)}′` : ""}${rec.cook_min != null ? ` · cottura ${esc(rec.cook_min)}′` : ""}</div>
      ${nut}
      ${ing ? `<div class="reclab">Ingredienti</div><ul>${ing}</ul>` : ""}
      ${steps ? `<div class="reclab">Preparazione</div><ol>${steps}</ol>` : ""}</div>`;
  };

  const dietBlock = (r.diet && r.diet.days && r.diet.days.length) ? r.diet.days.map((d) => `
    <div class="day">
      <div class="dayhead">${esc(d.day)} <span>${esc(d.total_kcal || "")} kcal</span></div>
      ${(d.meals || []).map((m) => `
        <div class="meal">
          <div class="mealhead"><b>${esc(m.type)}</b> — ${esc(m.name)} <span>${esc(m.kcal || "")} kcal</span></div>
          <ul>${(m.items || []).map((it) => `<li>${esc(it.food)} <span class="muted">${esc(it.grams)}g · ${esc(it.kcal)} kcal</span></li>`).join("")}</ul>
          ${suppByMeal(m.type)}
          ${recipeFor(m.name)}
        </div>`).join("")}
    </div>`).join("") : "<p class='muted'>Nessuna dieta generata.</p>";

  const suppBlock = supps.length ? `<table><thead><tr><th>Integratore</th><th>Dose</th><th>Pasto</th><th>Priorità</th></tr></thead>
    <tbody>${supps.map((s) => `<tr><td>${esc(s.name)}</td><td>${esc(s.dosage || "")}</td><td>${esc(s.meal || s.timing || "")}</td><td>${esc(s.priority || "")}</td></tr>`).join("")}</tbody></table>`
    : "<p class='muted'>Nessun integratore consigliato.</p>";

  // Blood: marker trend charts (markers with >=2 numeric points across reports, oldest->newest) + tables
  const bloodTests = (r.blood_tests || []).slice().sort((a, b) => (a.date || "").localeCompare(b.date || ""));
  const byMarker = {};
  bloodTests.forEach((bt) => (bt.results || []).forEach((res) => {
    const n = num(res.value);
    if (n === null || !res.marker) return;
    (byMarker[res.marker] = byMarker[res.marker] || []).push({ label: (bt.date || "").slice(5, 10), value: n });
  }));
  const markerCharts = Object.entries(byMarker).filter(([, pts]) => pts.length >= 2).slice(0, 8)
    .map(([name, pts]) => `<div class="chart"><h4>${esc(name)}</h4>${svgLine(pts, "#EC0B8C")}</div>`).join("");
  const bloodBlock = (r.blood_tests && r.blood_tests.length) ? `
    ${markerCharts ? `<div class="charts">${markerCharts}</div>` : ""}
    ${r.blood_tests.map((bt) => {
      const it = bt.interpretation || {};
      const rows = (it.markers || []).map((m) => `<tr><td>${esc(m.marker)}</td><td>${esc(m.value)} ${esc(m.unit || "")}</td><td class="st-${esc(m.status)}">${esc(m.status || "")}</td><td>${esc(m.reference || "")}</td></tr>`).join("");
      return `<div class="btest"><div class="dayhead">${esc(bt.panel_name || "Analisi")} <span>${esc((bt.date || "").slice(0, 10))}</span></div>
        <table><thead><tr><th>Valore</th><th>Risultato</th><th>Stato</th><th>Riferimento</th></tr></thead><tbody>${rows}</tbody></table>
        ${it.overall ? `<p><b>Valutazione:</b> ${esc(it.overall)}</p>` : ""}
        ${(it.nutrition_advice || []).length ? `<p><b>Consigli:</b></p>${list(it.nutrition_advice)}` : ""}</div>`;
    }).join("")}` : "<p class='muted'>Nessuna analisi caricata.</p>";

  const weights = r.weights || [];
  const wPoints = weights.map((w) => ({ label: (w.date || "").slice(5, 10), value: num(w.weight_kg) })).filter((x) => x.value !== null);
  const weightBlock = weights.length ? `
    ${wPoints.length >= 2 ? `<div class="chart">${svgLine(wPoints, "#2E5BFF")}</div>` : ""}
    <table><thead><tr><th>Data</th><th>Peso</th></tr></thead>
    <tbody>${weights.map((w) => `<tr><td>${esc((w.date || "").slice(0, 10))}</td><td>${esc(w.weight_kg)} kg</td></tr>`).join("")}</tbody></table>
    <p class="muted">Variazione totale: ${(weights[weights.length - 1].weight_kg - weights[0].weight_kg).toFixed(1)} kg</p>` : "<p class='muted'>Nessun dato di peso.</p>";

  const sec = (flag, title, html) => flag ? `<h2>${title}</h2>${html}` : "";

  return `<!doctype html><html><head><meta charset="utf-8"><title>Report DietFlix</title>
  <style>
    *{box-sizing:border-box} body{font-family:-apple-system,Segoe UI,Roboto,Arial,sans-serif;color:#1a1a2e;margin:0;padding:32px;font-size:13px}
    h1{font-size:26px;margin:0} h2{font-size:17px;margin:26px 0 10px;padding-bottom:6px;border-bottom:2px solid #2E5BFF;color:#2E5BFF}
    h4{margin:8px 0 4px;font-size:13px} .muted{color:#888} .k{color:#666}
    .head{display:flex;justify-content:space-between;align-items:flex-end;border-bottom:3px solid #EC0B8C;padding-bottom:10px}
    .brand{font-weight:800;color:#2E5BFF;font-size:22px}
    .grid2{display:grid;grid-template-columns:1fr 1fr;gap:4px 24px;margin:8px 0}
    .targets{display:flex;gap:16px;flex-wrap:wrap;background:#f3f5ff;border-radius:10px;padding:10px 14px;margin-top:10px}
    .day,.btest{border:1px solid #e5e7f0;border-radius:10px;padding:10px 14px;margin-bottom:10px;break-inside:avoid}
    .dayhead{font-weight:700;font-size:15px;display:flex;justify-content:space-between;color:#2E5BFF}
    .meal{margin:8px 0;padding-left:10px;border-left:3px solid #f0f0f5} .mealhead{display:flex;justify-content:space-between}
    ul{margin:4px 0;padding-left:18px} li span{float:right}
    .supp{background:#eef3ff;border-radius:6px;padding:3px 8px;margin:3px 0;color:#2E5BFF;font-size:12px}
    .recipe{margin-top:6px;background:#fff7fb;border:1px solid #f6d6e8;border-radius:8px;padding:6px 10px}
    .rectitle{font-weight:700;color:#EC0B8C;font-size:12px} .reclab{font-weight:600;margin-top:4px;font-size:12px}
    .recipe ul,.recipe ol{margin:2px 0;padding-left:18px} .recipe li{font-size:12px}
    table{width:100%;border-collapse:collapse;margin:6px 0} th,td{border:1px solid #e5e7f0;padding:5px 8px;text-align:left;font-size:12px}
    th{background:#f7f8fc} .st-alto{color:#c0392b;font-weight:700} .st-basso{color:#2980b9;font-weight:700} .st-normale{color:#27ae60}
    .charts{display:flex;flex-wrap:wrap;gap:14px} .chart{border:1px solid #eef;border-radius:10px;padding:8px 10px;break-inside:avoid}
    @media print{body{padding:16px}}
  </style></head><body>
    <div class="head"><div><h1>${esc(title)}</h1><div class="muted">${esc(r.name || "")} · ${esc(r.email || "")}</div></div>
      <div class="brand">DietFlix</div></div>
    <div class="muted" style="margin-top:6px">Generato il ${new Date().toLocaleDateString("it-IT")}</div>
    ${opts.subtitle ? `<p class="muted">${esc(opts.subtitle)}</p>` : ""}
    ${sec(on("profile"), "Profilo e fabbisogni", profileBlock)}
    ${sec(on("insights"), "Considerazioni sullo stile di vita", insightsBlock)}
    ${sec(on("diet"), "Dieta settimanale", dietBlock)}
    ${sec(on("supplements"), "Integratori consigliati", suppBlock)}
    ${sec(on("blood"), "Analisi del sangue", bloodBlock)}
    ${sec(on("weight"), "Andamento del peso", weightBlock)}
    <p class="muted" style="margin-top:24px;border-top:1px solid #eee;padding-top:10px">Documento indicativo generato da DietFlix. Non sostituisce il parere del medico.</p>
  </body></html>`;
}
